import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  FaHome, 
  FaExclamationTriangle, 
  FaClock, 
  FaDollarSign, 
  FaChartLine,
  FaCalendarAlt,
  FaPlus,
  FaCreditCard
} from 'react-icons/fa';
import NewRentalContract from '../../Views/RentalManagement/NewRentalContract/NewRentalContract';
import RegisterPayment from '../../Views/RentalManagement/RegisterPayment/RegisterPayment';
import TenantManagement from '../../Views/RentalManagement/TenantManagement/TenantManagement';
import style from './RentalDashboard.module.css';

const RentalDashboard = ({ data }) => {
  const navigate = useNavigate();
  const [activeModal, setActiveModal] = useState(null);
  const [enrichedPayments, setEnrichedPayments] = useState([]);
  const [tenantModal, setTenantModal] = useState({ show: false, tenants: [], loading: false });
  const [paymentsView, setPaymentsView] = useState('upcoming');
  const [filteredPayments, setFilteredPayments] = useState([]);

  const fetchTenants = async (rentalId) => {
    setTenantModal({ show: true, tenants: [], loading: true });
    try {
      const response = await axios.get(`/rentals/admin/${rentalId}/tenants`);
      setTenantModal({ 
        show: true, 
        tenants: response.data.data || [], 
        loading: false 
      });
    } catch (error) {
      console.error('Error obteniendo inquilinos:', error);
      setTenantModal({ 
        show: true, 
        tenants: [], 
        loading: false 
      });
    }
  };

  useEffect(() => {
    if (data?.upcomingPayments?.length) {
      setEnrichedPayments(data.upcomingPayments);
      filterPayments('upcoming', data.upcomingPayments);
    } else {
      setEnrichedPayments([]);
      setFilteredPayments([]);
    }
  }, [data?.upcomingPayments]);

  const filterPayments = (view, payments = enrichedPayments) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let filtered = [];
    
    switch(view) {
      case 'upcoming':
        filtered = payments.filter(p => {
          const dueDate = new Date(p.dueDate);
          return p.paymentStatus === 'pending' && dueDate >= now;
        });
        break;
      case 'overdue':
        filtered = payments.filter(p => {
          const dueDate = new Date(p.dueDate);
          return p.paymentStatus === 'overdue' || (p.paymentStatus === 'pending' && dueDate < now);
        });
        break;
      case 'pending':
        filtered = payments.filter(p => {
          const dueDate = new Date(p.dueDate);
          return p.paymentStatus === 'pending' && 
                 dueDate.getMonth() === currentMonth && 
                 dueDate.getFullYear() === currentYear;
        });
        break;
      default:
        filtered = payments;
    }
    
    setFilteredPayments(filtered);
    setPaymentsView(view);
  };
  
  if (!data || !data.summary) {
    return (
      <div className={style.dashboard}>
        <p className={style.noData}>No hay datos disponibles del dashboard</p>
      </div>
    );
  }

  const { summary } = data;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  return (
    <div className={style.dashboard}>
      <div className={style.metricsGrid}>
        <div className={`${style.metricCard} ${style.active}`}>
          <div className={style.cardHeader}>
            <FaHome className={style.cardIcon} />
            <h3>Contratos Activos</h3>
          </div>
          <div className={style.cardValue}>{summary.activeContracts || 0}</div>
          <Link to="/alquileres/contratos" className={style.cardLink}>
            Ver todos →
          </Link>
        </div>

        <div
          className={`${style.metricCard} ${style.warning} ${style.clickableCard}`}
          role="button"
          tabIndex={0}
          onClick={() => navigate('/alquileres/por-vencer')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/alquileres/por-vencer');
            }
          }}
        >
          <div className={style.cardHeader}>
            <FaExclamationTriangle className={style.cardIcon} />
            <h3>Por Vencer</h3>
          </div>
          <div className={style.cardValue}>{summary.expiringContracts || 0}</div>
          <Link
            to="/alquileres/por-vencer"
            className={style.cardLink}
            onClick={(e) => e.stopPropagation()}
          >
            Ver detalles →
          </Link>
        </div>

        <div
          className={`${style.metricCard} ${style.danger} ${style.clickableCard}`}
          role="button"
          tabIndex={0}
          onClick={() => navigate('/alquileres/vencidos')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/alquileres/vencidos');
            }
          }}
        >
          <div className={style.cardHeader}>
            <FaClock className={style.cardIcon} />
            <h3>Pagos Vencidos</h3>
          </div>
          <div className={style.cardValue}>{summary.overduePayments || 0}</div>
          <div className={style.cardSubvalue}>
            {formatCurrency(summary.totalOverdue || 0)}
          </div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              navigate('/alquileres/vencidos');
            }}
            className={style.cardLink}
          >
            Gestionar →
          </button>
        </div>

        <div
          className={`${style.metricCard} ${style.pending} ${style.clickableCard}`}
          role="button"
          tabIndex={0}
          onClick={() => navigate('/alquileres/pendientes-mes')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/alquileres/pendientes-mes');
            }
          }}
        >
          <div className={style.cardHeader}>
            <FaCalendarAlt className={style.cardIcon} />
            <h3>Pendientes Este Mes</h3>
          </div>
          <div className={style.cardValue}>{summary.pendingPaymentsInRange ?? 0}</div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              navigate('/alquileres/pendientes-mes');
            }}
            className={style.cardLink}
          >
            Ver lista →
          </button>
        </div>

        <div className={`${style.metricCard} ${style.success}`}>
          <div className={style.cardHeader}>
            <FaDollarSign className={style.cardIcon} />
            <h3>Cobrado Este Mes</h3>
          </div>
          <div className={style.cardValue}>
            {formatCurrency(summary.collectedThisMonth || 0)}
          </div>
          <Link to="/alquileres/reportes" className={style.cardLink}>
            Ver reportes →
          </Link>
        </div>

        <div className={`${style.metricCard} ${style.info}`}>
          <div className={style.cardHeader}>
            <FaChartLine className={style.cardIcon} />
            <h3>Próximos Ajustes</h3>
          </div>
          <div className={style.cardValue}>{summary.upcomingAdjustments || 0}</div>
          <Link to="/alquileres/ajustes" className={style.cardLink}>
            Gestionar ajustes →
          </Link>
        </div>
      </div>

      <div className={style.quickActions}>
        <h3>Acciones Rápidas</h3>
        <div className={style.actionGrid}>
          <button 
            type="button"
            onClick={() => setActiveModal('contract')}
            className={style.actionButton}
          >
            <FaPlus /> Nuevo Contrato
          </button>
        </div>
      </div>

      {enrichedPayments.length > 0 && (
        <div className={style.upcomingPayments}>
          <div className={style.paymentsHeader}>
            <h3>
              {paymentsView === 'upcoming' && 'Próximos Pagos'}
              {paymentsView === 'overdue' && 'Pagos Vencidos'}
              {paymentsView === 'pending' && 'Pagos Pendientes del Mes'}
            </h3>
            <div className={style.viewFilters}>
              <button 
                className={`${style.filterBtn} ${paymentsView === 'upcoming' ? style.active : ''}`}
                onClick={() => filterPayments('upcoming')}
              >
                Por Vencer
              </button>
              <button 
                className={`${style.filterBtn} ${paymentsView === 'overdue' ? style.active : ''}`}
                onClick={() => filterPayments('overdue')}
              >
                Vencidos
              </button>
              <button 
                className={`${style.filterBtn} ${paymentsView === 'pending' ? style.active : ''}`}
                onClick={() => filterPayments('pending')}
              >
                Pendientes del Mes
              </button>
            </div>
          </div>
          <div className={style.paymentsTable}>
            <div className={style.tableHeader}>
              <span>Propiedad</span>
              <span>Propietario</span>
              <span>Inquilinos</span>
              <span>Vencimiento</span>
              <span>Monto</span>
              <span>Estado</span>
              <span>Acciones</span>
            </div>
            {filteredPayments.length > 0 ? (
              filteredPayments.slice(0, 10).map((payment, index) => {
                const isOverdue = new Date(payment.dueDate) < new Date() && payment.paymentStatus === 'pending';
                const displayStatus = isOverdue ? 'overdue' : payment.paymentStatus;
                
                return (
                  <div key={payment.id || index} className={`${style.tableRow} ${isOverdue ? style.overdueRow : ''}`}>
                    <div className={style.property}>
                      <div className={style.propertyTitle}>
                        ID: {payment.rental?.property?.id}
                      </div>
                      <div className={style.propertyDetails}>
                        <span className={style.propertyAddress}>
                          📍 {payment.rental?.property?.street} {payment.rental?.property?.number}, {payment.rental?.property?.locality}
                        </span>
                        <span className={style.propertyType}>
                          🏠 {payment.rental?.property?.propertyType}
                        </span>
                      </div>
                    </div>
                    <span className={style.owner}>
                      {payment.rental?.ownerName || 'Sin propietario'}
                    </span>
                    <div className={style.tenantActions}>
                      <button 
                        onClick={() => fetchTenants(payment.rentalId)}
                        className={style.tenantsButton}
                      >
                        Ver Inquilinos
                      </button>
                    </div>
                    <span className={style.dueDate}>
                      {formatDate(payment.dueDate)}
                    </span>
                    <span className={style.amount}>
                      {formatCurrency(payment.totalAmount)}
                    </span>
                    <span className={`${style.status} ${style[displayStatus]}`}>
                      {displayStatus === 'pending' ? 'Pendiente' : 
                       displayStatus === 'overdue' ? 'Vencido' : 
                       displayStatus === 'paid' ? 'Pagado' : 
                       displayStatus}
                    </span>
                    <div className={style.actionButtons}>
                      {displayStatus !== 'paid' && (
                        <button 
                          onClick={() => setActiveModal('payment')}
                          className={style.payButton}
                          title="Registrar pago"
                        >
                          <FaCreditCard />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={style.noData}>
                No hay {paymentsView === 'upcoming' ? 'pagos próximos' : 
                        paymentsView === 'overdue' ? 'pagos vencidos' : 
                        'pagos pendientes'} para mostrar
              </div>
            )}
          </div>
        </div>
      )}

      {tenantModal.show && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <div className={style.modalHeader}>
              <h3>Lista de Inquilinos</h3>
              <button 
                onClick={() => setTenantModal({ show: false, tenants: [], loading: false })}
                className={style.closeButton}
              >
                ×
              </button>
            </div>
            <div className={style.modalContent}>
              {tenantModal.loading ? (
                <p>Cargando inquilinos...</p>
              ) : tenantModal.tenants.length > 0 ? (
                <div className={style.tenantsList}>
                  {tenantModal.tenants.map((tenant, index) => (
                    <div key={tenant.id || index} className={style.tenantItem}>
                      <div className={style.tenantInfo}>
                        <strong>{tenant.fullName || `${tenant.firstName} ${tenant.lastName}`}</strong>
                        <span className={style.tenantType}>
                          {tenant.tenantType === 'main' ? 'Inquilino Principal' : 
                           tenant.tenantType === 'guarantor' ? 'Garante' : 
                           tenant.tenantType === 'co-tenant' ? 'Co-inquilino' : 
                           tenant.tenantType}
                        </span>
                        {tenant.email && <span>📧 {tenant.email}</span>}
                        {tenant.phone && <span>📞 {tenant.phone}</span>}
                        {tenant.dni && <span>🆔 DNI: {tenant.dni}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No hay inquilinos registrados para este contrato.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeModal === 'contract' && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <NewRentalContract onClose={() => setActiveModal(null)} />
          </div>
        </div>
      )}

      {activeModal === 'payment' && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <RegisterPayment onClose={() => setActiveModal(null)} />
          </div>
        </div>
      )}

      {activeModal === 'tenant' && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <TenantManagement onClose={() => setActiveModal(null)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalDashboard;
